import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { MenuItem, Overlay, Tooltip } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { updateLeadFields } from 'bundles/project/actions/leads';
import { createDropdownItems } from './helper';
import AccessLevelToShow from './access_level_to_show';
import UnstyledDropdownToggle from 'components/bootstrap_extensions/unstyled_dropdown_toggle';
import Dropdown from 'components/dropdown';
import Button from 'shared/button';

function DocumentProgress({ columnName, lead, leaseAccessLevels, saleAccessLevels, onUpdate, externalOnUpdate }) {
  const [saving, setSaving] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const buttonRef = useRef();
  const isSale = columnName.toLowerCase().includes('sale');
  const accessLevels = isSale ? saleAccessLevels : leaseAccessLevels;

  const psuedoColumnName = isSale ?
    'saleVaultGatesAccessLevelWithoutOverride' :
    'leaseVaultGatesAccessLevelWithoutOverride';
  const psuedoValue = lead[psuedoColumnName];
  const value = lead[columnName];
  const isPinned = value !== 'no_override';
  const uniqueId = `${psuedoColumnName}-${lead.id}`;
  const valueToShow = isPinned ? value : psuedoValue;

  if (valueToShow === 'no_override') return null;

  if (saving) {
    return (
      <div className="text-center">
        <FontAwesomeIcon icon={faSpinner} size="lg" spin/>
      </div>
    );
  }

  if (!accessLevels || accessLevels.length === 0) {
    return <AccessLevelToShow isPinned={isPinned} uniqueId={uniqueId} valueToShow={valueToShow}/>;
  }

  const modifyAccessLevel = (value) => {
    setSaving(true);
    setShowOverlay(false);

    if (externalOnUpdate) {
      externalOnUpdate(lead, { [columnName]: value });
      setSaving(false);
    } else {
      onUpdate(lead, { [columnName]: value }).finally(() => setSaving(false));
    }
  };

  const dropdownMenuItems = createDropdownItems(isPinned, value, modifyAccessLevel, accessLevels);

  return (
    <div className="d-flex justify-content-between align-items-center">
      <AccessLevelToShow isPinned={isPinned} uniqueId={uniqueId} valueToShow={valueToShow}/>
      <Overlay
        placement="top"
        rootClose
        show={showOverlay}
        target={buttonRef.current}
        onHide={() => setShowOverlay(false)}
      >
        <Tooltip id={`tooltip-${uniqueId}`}>
          Note: overriding a lead's progress will lock them to that
          progress. They will not be able to progress further until they are unpinned
        </Tooltip>
      </Overlay>
      <Dropdown absolute={true} className="lead-options" id={`dropdown-${uniqueId}`}>
        <UnstyledDropdownToggle bsRole="toggle">
          <Button.Secondary plain ref={buttonRef} type="button" onClick={() => setShowOverlay(true)}>
            <FontAwesomeIcon className="text-primary" icon={faEdit} size="lg"/>
          </Button.Secondary>
        </UnstyledDropdownToggle>
        <Dropdown.Menu className="dropdown-menu-right">
          {dropdownMenuItems.map(item => (
            <MenuItem key={item.key} onClick={item.onClick}>
              {item.content}
            </MenuItem>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

DocumentProgress.propTypes = {
  columnName: PropTypes.string,
  externalOnUpdate: PropTypes.func,
  lead: PropTypes.object,
  leaseAccessLevels: PropTypes.arrayOf(PropTypes.string),
  saleAccessLevels: PropTypes.arrayOf(PropTypes.string),
  onUpdate: PropTypes.func
};

const mapStateToProps = ({ project }) => ({
  saleAccessLevels: project?.saleVault?.accessLevels,
  leaseAccessLevels: project?.leaseVault?.accessLevels
});

const mapDispatchToProps = {
  onUpdate: updateLeadFields
};



export default connect(mapStateToProps, mapDispatchToProps)(DocumentProgress);
